import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Box, Grid, makeStyles, Typography } from "@material-ui/core";

import { COLORS } from "../../../../../theme/colors";
import { Table } from "../../../../../components/table";
import { Pagination } from "../../../../../components/pagination";

import SearchIcon from "../../../../../assets/images/icons/search.svg";
import { Icon, UnderscoreTextInput } from "../../../../../components/base";
import { getData, getQuarter, getYear } from "../../../../../services";
import { ANY_FILTER_KEY, ITEMS_PER_PAGE } from "../../../constants";
import { EmptyPage, TableFilter } from "../../../../../components/parts";
import { ActionButtonsGroup } from "../../../../../components/buttonsGroup/ActionButtonsGroup";
import { TENANTS_FILTERS } from "../../../../../services/utils";
import { paginationOfExpandableList } from "../../../../building/tabsV2/units/unitListRow";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  titleContainer: {
    position: "relative",
    "& >p:first-child": {
      position: "absolute",
      left: -10,
    },
  },
  filterTitle: {
    color: COLORS.darks[0][50],
  },
  filterOptionsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filterOption: {
    marginBottom: theme.spacing(2),
  },
  optionText: {
    marginRight: theme.spacing(0.5),
    cursor: "pointer",
  },
  searchContainer: {
    marginBottom: theme.spacing(4),
  },
  searchBtn: {
    marginLeft: theme.spacing(1),
  },
  pagination: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
}));

const defaultFilters = {
  electricity_status: ANY_FILTER_KEY,
  leasing_end: ANY_FILTER_KEY,
  square: ANY_FILTER_KEY,
};

export const TenantsList = ({
  buildingId,
  complexId,
  setShowTenantList,
  formatter,
  propertyFlag = true,
}) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const [tenants, setTenants] = useState(null);
  const [tenantsCnt, setTenantsCnt] = useState(0);
  const [tableColumnWidths, setTableColumnWidths] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [paginationCalculated, setPaginationCalculated] = useState({
    cnt: 0,
    pageSizes: [],
    pageStarts: [],
    pageEnds: [],
  });
  const [tenantFilters, setTenantFilters] = useState(TENANTS_FILTERS(user?.unit_system));
  const isGrouped = buildingId ? false : true;
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  useEffect(() => {
    const url = buildingId
      ? `/praas/v2/buildings/${buildingId}/properties/tenants/`
      : complexId
      ? `/praas/v2/complexes/${complexId}/properties/tenants/`
      : "/praas/v2/buildings/properties/tenants/";

    getData(url, buildingId ? setTenants : setTenantsGrouped);

    return () => setTenants([]);
  }, [buildingId, complexId, cluster, segments, selectedCountries]);

  const filtersCount = useMemo(() => {
    const thisQ = getQuarter();
    const thisYear = getYear();
    const count = {
      this_q: 0,
      this_year: 0,
      square_large: 0,
      square_mid: 0,
      square_small: 0,
    };
    if (tenants === null) {
      return 0;
    }
    for (const tenant of tenants) {
      count[tenant.electricity_status?.status] =
        (count[tenant.electricity_status?.status] || 0) + 1;
      if (thisYear === getYear(new Date(tenant.leasing_end))) {
        count.this_year += 1;
        if (thisQ === getQuarter(new Date(tenant.leasing_end))) {
          count.this_q += 1;
        }
      }

      if (tenant.square < (user?.has_sqm_unit ? 30 : 2500)) {
        count.square_small += 1;
      } else if (
        tenant.square >= (user?.has_sqm_unit ? 30 : 2500) &&
        tenant.square < (user?.has_sqm_unit ? 40 : 5000)
      ) {
        count.square_mid += 1;
      } else if (tenant.square >= (user?.has_sqm_unit ? 40 : 5000)) {
        count.square_large += 1;
      }
    }
    return count;
  }, [tenants]);

  const filterTenants = useMemo(() => {
    let show_flag = false;
    return tenants?.filter((tenant) => {
      // show/hide decision based on search text
      if (searchText !== "" && searchText.length > 2) {
        if (isGrouped && tenant?.tenants) {
          const tmp = tenant.tenants;
          if (
            tmp.filter((val) => val.name.toLowerCase().includes(searchText.toLowerCase()) > 0)
              .length > 0
          ) {
            show_flag = true;
            tenant.expanded = true;
          } else if (
            tenant.name.toLowerCase().includes(searchText.toLowerCase()) ||
            tenant.address.toLowerCase().includes(searchText.toLowerCase())
          ) {
            show_flag = false;
            return true;
          } else {
            show_flag = false;
            tenant.expanded = false;
          }
        } else if (!isGrouped) {
          if (tenant.unit_id.toLowerCase().includes(searchText.toLowerCase())) {
            show_flag = true;
            tenant.expanded = true;
          } else {
            show_flag = false;
            tenant.expanded = false;
          }
        }
        // if search by text filtering by values (code below) ignored
        if (show_flag === false) {
          return false;
        }
      }
      // otherwise decide based on user selections
      else if (typeof tenant.expanded !== "undefined") {
        show_flag = tenant.expanded;
      }

      if (isGrouped && show_flag === false && tenant.isGroup === undefined) {
        return false;
      }
      if (
        filters.electricity_status !== ANY_FILTER_KEY &&
        (tenant?.tenants || []).some(
          (t) => t.electricity_status?.status !== filters.electricity_status
        )
      ) {
        return false;
      }

      if (
        filters.leasing_end === "this_year" &&
        tenant.leasing_end &&
        getYear() !== getYear(new Date(tenant.leasing_end))
      ) {
        return false;
      }

      if (
        filters.leasing_end === "this_q" &&
        tenant.leasing_end &&
        (getYear() !== getYear(new Date(tenant.leasing_end)) ||
          getQuarter() !== getQuarter(new Date(tenant.leasing_end)))
      ) {
        return false;
      }

      if (filters.square === "square_large" && tenant.square < (user?.has_sqm_unit ? 40 : 5000)) {
        return false;
      }
      if (
        filters.square === "square_mid" &&
        ((user?.has_sqm_unit ? 30 : 2500) > tenant.square ||
          tenant.square >= (user?.has_sqm_unit ? 40 : 5000))
      ) {
        return false;
      }
      if (filters.square === "square_small" && tenant.square >= (user?.has_sqm_unit ? 30 : 2500)) {
        return false;
      }

      return true;
    });
  }, [tenants, filters, searchText]);

  const selectTenant = (e, i) => {
    e.stopPropagation();
    expandCollapse(i);
  };

  useEffect(() => {
    if (filterTenants === undefined) return;
    const columns = filterTenants.map((e) =>
      e.isGroup ? formatter.groupColWidths : formatter.colWidths
    );
    setTableColumnWidths(columns);

    setPaginationCalculated(
      paginationOfExpandableList(filterTenants, 3, "isGroup", "tenants", "expanded")
    );
  }, [filterTenants]);

  const tableData = useMemo(() => {
    const items = filterTenants?.slice(
      paginationCalculated.pageStarts[page],
      paginationCalculated.pageEnds[page]
    );
    formatter = formatter(user?.unit_system);
    const res = items?.map((item, i) => {
      if (item?.isGroup) {
        return formatter.generateGroupRow(item, i, selectTenant);
      } else {
        return formatter.generateRow(item, i, currencySymbol);
      }
    });
    return res;
  }, [page, filterTenants, tenants, tableColumnWidths, paginationCalculated]);

  const onOptionSelect = (filterKey, optionKey) => {
    const newFilters = { ...filters, [filterKey]: optionKey };
    setFilters(newFilters);
    setPage(0);
  };

  const setTenantsGrouped = (value) => {
    const res = value.map((e) => ({ ...e, isGroup: true, expanded: false }));
    const tenantsCnt = res.reduce((acc, item) => {
      acc += item.tenants?.length || 0;
      return acc;
    }, 0);
    setTenantsCnt(tenantsCnt);
    const len = res.length;
    for (var i = len - 1; i >= 0; i--) {
      if (res[i].tenants && Symbol.iterator in Object(res[i].tenants)) {
        res.splice(i + 1, 0, ...res[i].tenants);
      }
    }
    res[0].expanded = true;
    setTenants(res);
  };

  const expandCollapse = (i) => {
    const mappedIndex = tenants.findIndex(
      (e) => e.isGroup && e.id === filterTenants[i + paginationCalculated.pageStarts[page]].id
    );
    if (tenants[mappedIndex]?.expanded) {
      let tmp = [...tenants];
      tmp[mappedIndex].expanded = false;

      setTenants(tmp);
    } else {
      let tmpp = [...tenants];
      tmpp[mappedIndex].expanded = true;

      setTenants(tmpp);
    }
  };

  const setSearchTextCollapsable = (e) => {
    if (e === "") {
      const res = tenants.map((item) => {
        if (item?.expanded) {
          return { ...item, expanded: false };
        } else {
          return item;
        }
      });
      setTenants(res);
    }
    setSearchText(e);
  };

  return (
    <Grid className={classes.root} container spacing={2}>
      {propertyFlag ? (
        <Grid style={{ marginBottom: 40 }} container justifyContent="space-between">
          <Typography style={{ fontWeight: "bold" }}>
            TENANTS LIST ({!isGrouped ? (tenants ? tenants.length : 0) : tenantsCnt})
            <Typography
              component="span"
              className={classes.clearFiter}
              onClick={() => setFilters(defaultFilters)}
            >
              Clear filters
            </Typography>
          </Typography>
          <Box className={classes.backBlock}>
            <Typography onClick={() => setShowTenantList(false)} className={classes.backToList}>
              Back to Property data
            </Typography>
            <ActionButtonsGroup hideIntegrateBtn />
          </Box>
        </Grid>
      ) : (
        <div />
      )}
      {tenants !== null ? (
        tenants.length ? (
          <>
            <Grid item xs={2}>
              <TableFilter
                count={!isGrouped ? (tenants ? tenants.length : 0) : tenantsCnt}
                filters={filters}
                dataFilters={tenantFilters}
                filtersCount={filtersCount}
                onOptionSelect={onOptionSelect}
                setDataFilters={setTenantFilters}
              />
            </Grid>
            <Grid item xs={10}>
              <Grid className={classes.searchContainer} container>
                <UnderscoreTextInput
                  value={searchText}
                  onChange={setSearchTextCollapsable}
                  placeHolder="Search for tenant..."
                  width="50%"
                />
                <Icon root={classes.searchBtn} src={SearchIcon} hoverable />
              </Grid>
              <Table
                headers={formatter.headers}
                data={tableData}
                colWidths={formatter.colWidths}
                colWidthsVariable={
                  isGrouped
                    ? tableColumnWidths.slice(
                        paginationCalculated.pageStarts[page],
                        paginationCalculated.pageEnds[page]
                      )
                    : undefined
                }
                cellPaddingRight={0}
                cellStyles={[{ padding: "18px 0 18px 24px" }]}
                maxHeight={1000}
              />
              <Pagination
                root={classes.pagination}
                totalPage={
                  isGrouped
                    ? Math.ceil(paginationCalculated.pageSizes.length)
                    : Math.ceil(filterTenants.length / ITEMS_PER_PAGE)
                }
                onChange={setPage}
                page={page}
              />
            </Grid>{" "}
          </>
        ) : (
          <EmptyPage headerText="tenants" footerText="Tenant" />
        )
      ) : (
        <div />
      )}
    </Grid>
  );
};
